import React from 'react';
import PropTypes from 'prop-types';
import Dialog from "../overlay/Dialog";
import OverlayController from "../overlay/OverlayController";
import mutations from "../../graphql/mutations";
import {FlatButton, MenuItem, SelectField, TextField, Toggle} from "material-ui";
import IconButton from "../material/IconButton";
import utils from '../../utils/utils';
import {List} from 'immutable';
import { compose } from 'react-apollo';
import _ from 'lodash';

const WEBHOOK_TRIGGER_NAMES = [
    'item.state.added',
    'item.state.removed'
];

export default compose(
    mutations.createWebhookMutationHoc,
    mutations.updateWebhookMutationHoc,
    mutations.deleteWebhookMutationHoc
)(class WebhookDialog extends React.Component {

    static propTypes = {
        project: PropTypes.object.isRequired,
        webhook: PropTypes.object,
        createWebhook: PropTypes.func.isRequired,
        updateWebhook: PropTypes.func.isRequired,
        deleteWebhook: PropTypes.func.isRequired
    };

    static defaultProps = {};

    constructor(props) {
        super(props);

        this.onSubmit = this.onSubmit.bind(this);
        this.onCallbackUrlChange = this.onCallbackUrlChange.bind(this);
        this.onEnabledToggle = this.onEnabledToggle.bind(this);
        this.onEditTriggerClick = this.onEditTriggerClick.bind(this);
        this.onDeleteTriggerClick = this.onDeleteTriggerClick.bind(this);
        this.onTriggerNameChange = this.onTriggerNameChange.bind(this);
        this.onTriggerStateIdChange = this.onTriggerStateIdChange.bind(this);
        this.onTriggerDoneClick = this.onTriggerDoneClick.bind(this);
        this.onTriggerCancelClick = this.onTriggerCancelClick.bind(this);
        this.onWebhookDeleteClick = this.onWebhookDeleteClick.bind(this);

        this.state = {
            callbackUrl: _.get(this.props.webhook, 'callbackUrl', ''),
            enabled: _.get(this.props.webhook, 'enabled', true),
            triggers: List(_.get(this.props.webhook, 'triggers', []).map(trigger => _.pick(trigger, ['name', 'stateId']))),
            editedTriggerIndex: null,
            triggerName: null,
            triggerStateId: null
        };
    }

    onSubmit(event) {
        event.preventDefault();
        let submitPromise = null;
        let webhookInput = {
            enabled: this.state.enabled,
            callbackUrl: this.state.callbackUrl,
            triggers: this.state.triggers.toJS()
        };
        OverlayController.showActivityIndicator();
        if (this.props.webhook) {
            submitPromise = this.props.updateWebhook(this.props.webhook.id, webhookInput);
        } else {
            submitPromise = this.props.createWebhook(this.props.project.id, webhookInput);
        }
        submitPromise.then(() => {
            OverlayController.hideActivityIndicator();
            OverlayController.hideOverlay();
        }).catch((error) => {
            OverlayController.hideActivityIndicator();
            OverlayController.showErrorAlert(error);
        });
    }

    onCallbackUrlChange(event) {
        this.setState({
            callbackUrl: event.target.value
        });
    }

    onEnabledToggle(event, isInputChecked) {
        this.setState({
            enabled: isInputChecked
        });
    }

    onEditTriggerClick(event) {
        let triggerIndex = Number(utils.getValueAtPathOfClosestParent(event.currentTarget, 'dataset.triggerIndex'));
        let trigger = this.state.triggers.get(triggerIndex);
        this.setState({
            editedTriggerIndex: triggerIndex,
            triggerName: trigger.name,
            triggerStateId: trigger.stateId
        });
    }

    onDeleteTriggerClick(event) {
        let triggerIndex = Number(utils.getValueAtPathOfClosestParent(event.currentTarget, 'dataset.triggerIndex'));
        let actions = [
            {label: 'Cancel', handler: () => OverlayController.hideOverlay()},
            {label: 'Delete', handler: () => {
                this.setState({
                    triggers: this.state.triggers.remove(triggerIndex)
                });
                OverlayController.hideOverlay();
            }}
        ];
        OverlayController.showOverlay({
            component: <Dialog title={`Are you sure?`} actions={actions} />
        });
    }

    onTriggerNameChange(event, index, value) {
        let state = {
            triggerName: value,
            triggerStateId: null
        };
        if (this.state.editedTriggerIndex === null) {
            state.editedTriggerIndex = this.state.triggers.size;
        }
        this.setState(state);
    }

    onTriggerStateIdChange(event, index, value) {
        this.setState({
            triggerStateId: value
        });
    }

    onTriggerDoneClick() {
        this.setState({
            triggers: this.state.triggers.set(this.state.editedTriggerIndex, {
                name: this.state.triggerName,
                stateId: this.state.triggerStateId
            }),
            editedTriggerIndex: null,
            triggerName: null,
            triggerStateId: null
        });
    }

    onTriggerCancelClick() {
        this.setState({
            editedTriggerIndex: null,
            triggerName: null,
            triggerStateId: null
        });
    }

    onWebhookDeleteClick() {
        let actions = [
            {label: 'Cancel', handler: () => OverlayController.hideOverlay()},
            {label: 'Delete', handler: () => {
                OverlayController.hideOverlay();
                OverlayController.showActivityIndicator();
                this.props.deleteWebhook(this.props.webhook.id).then(() => {
                    OverlayController.hideActivityIndicator();
                    OverlayController.hideOverlay();
                }).catch((error) => {
                    OverlayController.hideActivityIndicator();
                    OverlayController.showErrorAlert(error);
                });
            }}
        ];
        OverlayController.showOverlay({
            component: <Dialog title={`Are you sure?`} actions={actions} />
        });
    }

    renderTriggers() {
        let statesById = _.keyBy(this.props.project.states, 'id');
        let triggers = this.state.triggers.toJS().map((trigger, index) => {
            let state = statesById[trigger.stateId];
            let name = trigger.name;
            if (this.state.editedTriggerIndex !== index) {
                return this.renderTriggerPlain(state, name, index)
            } else {
                return this.renderTriggerEdit(index)
            }
        });
        // Add one more row for creating new triggers
        triggers.push(this.renderTriggerEdit(this.state.triggers.size));
        return (
            <div className="materialList dense triggerList">{triggers}</div>
        );
    }

    renderTriggerPlain(state, name, index) {
        let disabled = this.state.editedTriggerIndex !== null;
        return (
            <div key={`plainTrigger-${index}`} className="materialListItem singleLine" data-trigger-index={index}>
                <div className="materialListItemText">{name}:{state.name}</div>
                <div className="materialListItemRightIcon">
                    <IconButton
                        icon={<i className="material-icons md-18">edit</i>}
                        disabled={disabled}
                        buttonSize={IconButton.ButtonSize.buttonSize40}
                        iconFontSize={IconButton.IconFontSize.iconFontSize18}
                        onClick={this.onEditTriggerClick}/>
                    <IconButton
                        icon={<i className="material-icons md-18">delete</i>}
                        disabled={disabled}
                        buttonSize={IconButton.ButtonSize.buttonSize40}
                        iconFontSize={IconButton.IconFontSize.iconFontSize18}
                        onClick={this.onDeleteTriggerClick}/>
                </div>
            </div>
        );
    }

    renderTriggerEdit(triggerIndex) {
        let triggerNamesMenuItems = WEBHOOK_TRIGGER_NAMES.map(triggerName => {
            return <MenuItem key={triggerName} value={triggerName} primaryText={triggerName}/>;
        });
        let stateMenuItems = this.props.project.states.filter(state => state.type === 'normal').map(state => {
            let idx = _.findIndex(this.state.triggers.toJS(), {
                name: this.state.triggerName,
                stateId: state.id
            });
            let disabled = (idx !== -1) && (idx !== this.state.editedTriggerIndex);
            return <MenuItem key={state.id} value={state.id} primaryText={state.name} disabled={disabled}/>;
        });
        let editingCurrent = this.state.editedTriggerIndex === null || triggerIndex === this.state.editedTriggerIndex;
        let triggerName = editingCurrent ? this.state.triggerName : null;
        let stateId = editingCurrent ? this.state.triggerStateId : null;
        return (
            <div key={`editTrigger-${triggerIndex}`} className="editTriggerRow">
                <SelectField
                    className="custom-field-no-label custom-select-field triggerNameSelect"
                    hintText="Choose Trigger"
                    value={triggerName}
                    autoWidth={true}
                    onChange={this.onTriggerNameChange}
                    disabled={!editingCurrent}
                >
                    {triggerNamesMenuItems}
                </SelectField>
                <SelectField
                    className="custom-field-no-label custom-select-field triggerStateSelect"
                    hintText="Choose State"
                    value={stateId}
                    autoWidth={true}
                    onChange={this.onTriggerStateIdChange}
                    disabled={!editingCurrent || this.state.triggerName === null}
                >
                    {stateMenuItems}
                </SelectField>
                <FlatButton
                    className="triggerDoneButton"
                    label="Done"
                    primary={true}
                    disabled={!editingCurrent || this.state.triggerStateId === null}
                    onClick={this.onTriggerDoneClick}
                />
                <FlatButton
                    className="triggerCancelButton"
                    label="Cancel"
                    primary={true}
                    disabled={!editingCurrent  || this.state.triggerName === null}
                    onClick={this.onTriggerCancelClick}
                />
            </div>
        );
    }

    isEnabled() {
        return this.state.callbackUrl.trim().length > 0
            && this.state.triggers.size > 0
            && this.state.editedTriggerIndex === null;
    }

    render() {
        let submitButtonLabel = this.props.webhook ? 'Save' : 'Create';
        let actions = [
            {label: 'Cancel', handler: () => OverlayController.hideOverlay()},
            {label: submitButtonLabel, type: "submit", form: "webhookForm", disabled: !this.isEnabled()}
        ];
        return (
            <Dialog showTitleBar={true} title="Webhook" actions={actions} className="webhookDialog">
                <form onSubmit={this.onSubmit} id="webhookForm">
                    <TextField
                        className="custom-field"
                        fullWidth={true}
                        floatingLabelText="Full Callback URL"
                        floatingLabelFixed={true}
                        hintText="Enter full callback URL, including http(s)://"
                        value={this.state.callbackUrl}
                        onChange={this.onCallbackUrlChange}
                    />

                    <Toggle
                        className="webhookEnabledToggle"
                        label="Enabled"
                        toggled={this.state.enabled}
                        onToggle={this.onEnabledToggle}
                    />

                    {this.renderTriggers()}

                    {this.props.webhook && <div className="webhookDialogDeleteButtonContainer"><FlatButton
                        className="webhookContainerDeleteButton"
                        label="Delete Webhook"
                        secondary={true}
                        onClick={this.onWebhookDeleteClick}
                        icon={<i className="material-icons md-18">delete</i>}
                    /></div>}
                </form>
            </Dialog>
        );
    }
});



// WEBPACK FOOTER //
// ./src/components/webhooks/WebhookDialog.js